<template>
  <v-sheet
    data-testid="report-header"
    color="white"
    elevation="2"
    class="pa-4 report-header__sheet blue-grey lighten-2"
  >
    <v-row class="report-header__content d-flex ma-0">
      <v-col cols="3" class="d-flex align-center">
        <p class="font-size-18 font-bold mb-0" v-html="sanitizedTitle"></p>
      </v-col>

      <v-col cols="9" class="d-flex justify-end align-center">
        <InsightsLastUpdatedLabel v-if="showLastUpdatedLabel" />

        <v-hover v-slot="{ hover }">
          <v-btn
            v-if="canExport"
            color="blue-grey lighten-3"
            class="ms-4"
            data-tourid="insights-export-button"
            :loading="isLoadingExport"
            :style="{
              transition: 'background-color 0.3s',
              'background-color': hover
                ? 'var(--v-blue-grey-lighten1) !important'
                : 'var(--v-blue-grey-lighten3) !important',
            }"
            @click="onExportButton"
          >
            <v-icon left color="blue-grey darken-1">save_alt</v-icon>

            <span class="">{{ $t('common.button.export_xlsx') }}</span>
          </v-btn>
        </v-hover>

        <v-hover v-slot="{ hover }">
          <v-btn
            color="blue-grey lighten-3"
            class="ms-4"
            data-tourid="insights-print-button"
            :style="{
              transition: 'background-color 0.3s',
              'background-color': hover
                ? 'var(--v-blue-grey-lighten1) !important'
                : 'var(--v-blue-grey-lighten3) !important',
            }"
            @click="onPrintButton"
          >
            <v-icon left color="blue-grey darken-1">mdi-printer-outline</v-icon>

            <span class="">{{ $t('common.button.print') }}</span>
          </v-btn>
        </v-hover>
      </v-col>

      <v-col cols="8">
        <p v-if="description" class="body-2 blue-grey--text text--darken-2">
          {{ description }}
        </p>
      </v-col>
    </v-row>
    <v-row v-show="controls" class="ma-0">
      <v-col col sm="12">
        <template v-for="control in controls">
          <v-select
            class="report-header__control report-select-control d-inline-block me-4 mt-4"
            background-color="blue-grey lighten-3"
            dense
            flat
            hide-details
            menu-props="offsetY, closeOnContentClick"
            outlined
            :value="selectedControls[control.key]"
            :key="control.key"
            :label="control.label"
            :items="control.options"
            @change="onControlChange($event, control.key)"
          >
          </v-select>
        </template>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters, mapMutations } from 'vuex'
import access from '@/lib/access.js'
import InsightsLastUpdatedLabel from '@/components/InsightsLastUpdatedLabel.vue'

const USERS_REPORT_TYPE = 'users'

export default {
  name: 'ReportHeader',

  components: {
    InsightsLastUpdatedLabel,
  },

  props: {
    showLastUpdatedLabel: Boolean,
    title: {
      type: String,
      required: false,
      default: '',
    },
    description: {
      type: String,
      required: false,
      default: '',
    },
    isLoadingExport: {
      type: Boolean,
      required: false,
    },
    hasExportableType: {
      type: Boolean,
      required: false,
      default: false,
    },
    controls: {
      type: Array,
      required: false,
      default: () => [],
    },
    controlsInitialValues: {
      type: Object,
      required: false,
      default: () => {},
    },
    reportType: {
      type: String,
      required: false,
    },
  },

  created() {
    if (_.isEmpty(this.selectedControls)) {
      this.updateActiveControls(this.controlsInitialValues)
    }
  },

  computed: {
    ...mapState('report', {
      activeControls: state => state.activeControls,
    }),

    ...mapGetters(['account']),

    hasPermissions() {
      return access.isLeadershipOrCoachOrFinancialCoach(this.account)
    },

    selectedControls() {
      return this.activeControls
    },

    sanitizedTitle() {
      return this.$sanitize(this.title)
    },
    canExport() {
      return (
        this.hasPermissions &&
        this.hasExportableType &&
        this.isXlsFeatureEnabled
      )
    },
    isXlsFeatureEnabled() {
      return (
        this.$isFeatureEnabled(this.$featuresKeys?.xlsReport) ||
        this.reportType !== USERS_REPORT_TYPE
      )
    },
  },

  methods: {
    ...mapMutations({
      updateActiveControls: 'report/updateActiveControls',
    }),

    onExportButton() {
      return this.$emit('onExport')
    },

    onPrintButton() {
      return this.$emit('onPrint')
    },

    onControlChange(value, key) {
      const controlToUpdate = {}
      controlToUpdate[key] = value
      this.updateActiveControls(controlToUpdate)
    },
  },
}
</script>

<style lang="scss" scoped>
.report-header {
  &__sheet {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &__control {
    max-width: 20%;
  }
}
</style>

<style lang="scss">
.report-select-control {
  label {
    background-color: var(--v-blue-grey-lighten2);
    border-radius: 6px;
    padding: 0 6px;
  }
}
</style>

<style lang="scss">
.report-header {
  &__export-button:hover {
    background-color: var(--v-blue-grey-darken1) !important;
  }
}
</style>
